/**
 * @file Helper methods for dealing with IndexedDB cache of messages, users, and topics.
 *
 * @copyright 2015-2022 Tinode LLC.
 */
'use strict';

import StringUtils from '../lib/string';

// NOTE TO DEVELOPERS:
// Localizable strings should be double quoted "строка на другом языке",
// non-localizable strings should be single quoted 'non-localized'.

const DB_VERSION = 12;
const DB_NAME = 'mocha-web';

let IDBProvider;

export default class DB {
  #onError = _ => { };
  #logger = _ => { };

  // Instance of IndexDB.
  db = null;
  // Indicator that the cache is disabled.
  disabled = true;

  constructor(onError, logger) {
    this.#onError = onError || this.#onError;
    this.#logger = logger || this.#logger;
  }

  #mapObjects(source, callback, context) {
    if (!this.db) {
      return disabled ?
        Promise.resolve([]) :
        Promise.reject(new Error("not initialized"));
    }

    return new Promise((resolve, reject) => {
      const trx = this.db.transaction([source]);
      trx.onerror = event => {
        this.#logger('PCache', 'mapObjects', source, event.target.error);
        reject(event.target.error);
      };
      trx.objectStore(source).getAll().onsuccess = event => {
        if (callback) {
          event.target.result.forEach(topic => {
            callback.call(context, topic);
          });
        }
        resolve(event.target.result);
      };
    });
  }

  /**
   * Initialize persistent cache: open or create/upgrade if needed.
   * @returns {Promise} promise to be resolved/rejected when the DB is initialized.
   */
  async initDatabase() {
    if (this.db) {
      this.db.close();
      this.db = null;
    }

    console.log('=================init db')
    return new Promise((resolve, reject) => {
      // Open the database and initialize callbacks.
      const req = IDBProvider.open(DB_NAME, DB_VERSION);

      req.onsuccess = event => {
        this.db = event.target.result;
        this.disabled = false;
        this.ensurePersistentStorage();
        this.db.onversionchange = (_) => {
          this.#logger('PCache', 'another tab tries to upgrade DB, shutting down');
          this.db.close();
          this.db = null;
          this.disabled = true;
        }
        resolve(this.db);
      };
      req.onerror = event => {
        this.#logger('PCache', "failed to initialize", event);
        reject(event.target.error);
        this.#onError(event.target.error);
      };
      req.onupgradeneeded = event => {
        this.db = event.target.result;

        this.db.onerror = event => {
          this.#logger('PCache', "failed to create storage", event);
          this.#onError(event.target.error);
        };

        // Individual object stores.
        // Object store (table) for topics. The primary key is topic name.
        if (!this.db.objectStoreNames.contains('topic')) {
          this.db.createObjectStore('topic', {
            keyPath: 'name'
          });
        }


        // Users object store. UID is the primary key.
        if (!this.db.objectStoreNames.contains('user')) {
          this.db.createObjectStore('user', {
            keyPath: 'uid'
          });
        }

        // Subscriptions object store topic <-> user. Topic name + UID is the primary key.
        if (!this.db.objectStoreNames.contains('subscription')) {
          this.db.createObjectStore('subscription', {
            keyPath: ['topic', 'uid']
          });
        }

        // Messages object store. The primary key is topic name + seq.
        if (!this.db.objectStoreNames.contains('message')) {
          this.db.createObjectStore('message', {
            keyPath: ['topic', 'seq']
          });
        }

        // Messages object store. The primary key is message id.
        if (!this.db.objectStoreNames.contains('message_index')) {
          this.db.createObjectStore('message_index', {
            keyPath: ['id']
          });
        }

        // Saved Messages object store. The primary key is saved timestamp.
        if (!this.db.objectStoreNames.contains('saved_message')) {
          this.db.createObjectStore('saved_message', {
            keyPath: 'savedTime'
          });
        }

        //Bảng lưu thông tin câu hỏi 
        if (!this.db.objectStoreNames.contains('poll_message')) {
          this.db.createObjectStore('poll_message', {
            keyPath: ['id']
          });
        }

        // Bảng lưu log
        if (!this.db.objectStoreNames.contains('weblog')) {
          this.db.createObjectStore('weblog', {
            keyPath: ['time', 'rand']
          });
        }
      };

      req.addEventListener('error', function (event) {
        console.error("Error opening database: ", event.target.errorCode);
        // Try to reopen the database
        // let retryInterval = 1000; // retry every 1 second
        // setTimeout(function () {
        //   this.initDatabase();
        // }, retryInterval);
        reject(event.target.errorCode);
      });
    });
  }

  /**
   * Delete persistent cache.
   */
  deleteDatabase() {
    // Close connection, otherwise operations will fail with 'onblocked'.
    if (this.db) {
      this.db.close();
      this.db = null;
    }
    return new Promise((resolve, reject) => {
      const req = IDBProvider.deleteDatabase(DB_NAME);
      req.onblocked = _ => {
        if (this.db) {
          this.db.close();
        }
        const err = new Error("blocked");
        this.#logger('PCache', 'deleteDatabase', err);
        reject(err);
      };
      req.onsuccess = _ => {
        this.db = null;
        this.disabled = true;
        resolve(true);
      };
      req.onerror = event => {
        this.#logger('PCache', 'deleteDatabase', event.target.error);
        reject(event.target.error);
      };
    });
  }

  /**
   * Check if persistent cache is ready for use.
   * @memberOf DB
   * @returns {boolean} <code>true</code> if cache is ready, <code>false</code> otherwise.
   */
  isReady() {
    return !!this.db;
  }

  // Topics.

  /**
   * Save to cache or update topic in persistent cache.
   * @memberOf DB
   * @param {Topic} topic - topic to be added or updated.
   * @returns {Promise} promise resolved/rejected on operation completion.
   */
  updTopic(topic) {
    if (!this.isReady()) {
      return this.disabled ?
        Promise.resolve() :
        Promise.reject(new Error("not initialized"));
    }
    return new Promise((resolve, reject) => {
      const trx = this.db.transaction(['topic'], 'readwrite');
      trx.oncomplete = event => {
        resolve(event.target.result);
      };
      trx.onerror = event => {
        this.#logger('PCache', 'updTopic', event.target.error);
        reject(event.target.error);
      };
      const req = trx.objectStore('topic').get(topic.name);
      req.onsuccess = _ => {
        trx.objectStore('topic').put(DB.#serializeTopic(req.result, topic));
        trx.commit();
      };
    });
  }

  /**
   * Mark or unmark topic as deleted.
   * @memberOf DB
   * @param {string} name - name of the topic to mark or unmark.
   * @param {boolean} deleted - status
   * @return {Promise} promise resolved/rejected on operation completion.
   */
  markTopicAsDeleted(name, deleted) {
    if (!this.isReady()) {
      return this.disabled ?
        Promise.resolve() :
        Promise.reject(new Error("not initialized"));
    }
    return new Promise((resolve, reject) => {
      const trx = this.db.transaction(['topic'], 'readwrite');
      trx.oncomplete = event => {
        resolve(event.target.result);
      };
      trx.onerror = event => {
        this.#logger('PCache', 'markTopicAsDeleted', event.target.error);
        reject(event.target.error);
      };
      const req = trx.objectStore('topic').get(name);
      req.onsuccess = event => {
        const topic = event.target.result;
        if (topic && topic._deleted != deleted) {
          topic._deleted = deleted;
          trx.objectStore('topic').put(topic);
        }
        trx.commit();
      };
    });
  }

  /**
   * Remove topic from persistent cache.
   * @memberOf DB
   * @param {string} name - name of the topic to remove from database.
   * @return {Promise} promise resolved/rejected on operation completion.
   */
  remTopic(name) {
    if (!this.isReady()) {
      return this.disabled ?
        Promise.resolve() :
        Promise.reject(new Error("not initialized"));
    }
    return new Promise((resolve, reject) => {
      const trx = this.db.transaction(['topic', 'subscription', 'message'], 'readwrite');
      trx.oncomplete = event => {
        resolve(event.target.result);
      };
      trx.onerror = event => {
        this.#logger('PCache', 'remTopic', event.target.error);
        reject(event.target.error);
      };
      trx.objectStore('topic').delete(IDBKeyRange.only(name));
      trx.objectStore('subscription').delete(IDBKeyRange.bound([name, '-'], [name, '~']));
      trx.objectStore('message').delete(IDBKeyRange.bound([name, 0], [name, Number.MAX_SAFE_INTEGER]));
      trx.commit();
    });
  }

  /**
   * Execute a callback for each stored topic.
   * @memberOf DB
   * @param {function} callback - function to call for each topic.
   * @param {Object} context - the value or <code>this</code> inside the callback.
   * @return {Promise} promise resolved/rejected on operation completion.
   */
  mapTopics(callback, context) {
    return this.#mapObjects('topic', callback, context);
  }

  /**
   * Copy data from serialized object to topic.
   * @memberOf DB
   * @param {Topic} topic - target to deserialize to.
   * @param {Object} src - serialized data to copy from.
   */
  deserializeTopic(topic, src) {
    DB.#deserializeTopic(topic, src);
  }

  // Users.
  /**
   * Add or update user object in the persistent cache.
   * @memberOf DB
   * @param {string} uid - ID of the user to save or update.
   * @param {Object} pub - user's <code>public</code> information.
   * @returns {Promise} promise resolved/rejected on operation completion.
   */
  updUser(uid, pub) {
    if (arguments.length < 2 || pub === undefined) {
      // No point inupdating user with invalid data.
      return;
    }
    if (!this.isReady()) {
      return this.disabled ?
        Promise.resolve() :
        Promise.reject(new Error("not initialized"));
    }
    return new Promise((resolve, reject) => {
      const trx = this.db.transaction(['user'], 'readwrite');
      trx.oncomplete = event => {
        resolve(event.target.result);
      };
      trx.onerror = event => {
        this.#logger('PCache', 'updUser', event.target.error);
        reject(event.target.error);
      };
      trx.objectStore('user').put({
        uid: uid,
        public: pub
      });
      trx.commit();
    });
  }

  /**
   * Remove user from persistent cache.
   * @memberOf DB
   * @param {string} uid - ID of the user to remove from the cache.
   * @return {Promise} promise resolved/rejected on operation completion.
   */
  remUser(uid) {
    if (!this.isReady()) {
      return this.disabled ?
        Promise.resolve() :
        Promise.reject(new Error("not initialized"));
    }
    return new Promise((resolve, reject) => {
      const trx = this.db.transaction(['user'], 'readwrite');
      trx.oncomplete = event => {
        resolve(event.target.result);
      };
      trx.onerror = event => {
        this.#logger('PCache', 'remUser', event.target.error);
        reject(event.target.error);
      };
      trx.objectStore('user').delete(IDBKeyRange.only(uid));
      trx.commit();
    });
  }

  /**
   * Execute a callback for each stored user.
   * @memberOf DB
   * @param {function} callback - function to call for each topic.
   * @param {Object} context - the value or <code>this</code> inside the callback.
   * @return {Promise} promise resolved/rejected on operation completion.
   */
  mapUsers(callback, context) {
    return this.#mapObjects('user', callback, context);
  }

  /**
   * Read a single user from persistent cache.
   * @memberOf DB
   * @param {string} uid - ID of the user to fetch from cache.
   * @return {Promise} promise resolved/rejected on operation completion.
   */
  getUser(uid) {
    if (!this.isReady()) {
      return this.disabled ?
        Promise.resolve() :
        Promise.reject(new Error("not initialized"));
    }
    return new Promise((resolve, reject) => {
      const trx = this.db.transaction(['user']);
      trx.oncomplete = event => {
        const user = event.target.result;
        resolve({
          user: user.uid,
          public: user.public
        });
      };
      trx.onerror = event => {
        this.#logger('PCache', 'getUser', event.target.error);
        reject(event.target.error);
      };
      trx.objectStore('user').get(uid);
    });
  }

  /**
 * Read a single user from persistent cache.
 * @memberOf DB
 * @param {string} uid - ID of the user to fetch from cache.
 * @return {Promise} promise resolved/rejected on operation completion.
 */
  getMessageIndex(id) {
    if (!this.isReady()) {
      return this.disabled ?
        Promise.resolve() :
        Promise.reject(new Error("not initialized"));
    }
    return new Promise((resolve, reject) => {
      const trx = this.db.transaction(['message_index'], 'readonly');
      trx.onerror = event => {
        this.#logger('PCache', 'getMsgIndex', event.target.error);
        reject(event.target.error);
      };
      trx.objectStore('message_index').get([id]).onsuccess = (event) => {
        const msgIndex = event.target.result;
        resolve(msgIndex);
      };
    });
  }

  // Subscriptions.
  /**
   * Add or update subscription in persistent cache.
   * @memberOf DB
   * @param {string} topicName - name of the topic which owns the message.
   * @param {string} uid - ID of the subscribed user.
   * @param {Object} sub - subscription to save.
   * @return {Promise} promise resolved/rejected on operation completion.
   */
  updSubscription(topicName, uid, sub) {
    if (!this.isReady()) {
      return this.disabled ?
        Promise.resolve() :
        Promise.reject(new Error("not initialized"));
    }
    return new Promise((resolve, reject) => {
      const trx = this.db.transaction(['subscription'], 'readwrite');
      trx.oncomplete = event => {
        resolve(event.target.result);
      };
      trx.onerror = event => {
        this.#logger('PCache', 'updSubscription', event.target.error);
        reject(event.target.error);
      };
      trx.objectStore('subscription').get([topicName, uid]).onsuccess = (event) => {
        trx.objectStore('subscription').put(DB.#serializeSubscription(event.target.result, topicName, uid, sub));
        trx.commit();
      };
    });
  }

  /**
   * Execute a callback for each cached subscription in a given topic.
   * @memberOf DB
   * @param {string} topicName - name of the topic which owns the subscriptions.
   * @param {function} callback - function to call for each subscription.
   * @param {Object} context - the value or <code>this</code> inside the callback.
   * @return {Promise} promise resolved/rejected on operation completion.
   */
  mapSubscriptions(topicName, callback, context) {
    if (!this.isReady()) {
      return this.disabled ?
        Promise.resolve([]) :
        Promise.reject(new Error("not initialized"));
    }
    return new Promise((resolve, reject) => {
      const trx = this.db.transaction(['subscription']);
      trx.onerror = (event) => {
        this.#logger('PCache', 'mapSubscriptions', event.target.error);
        reject(event.target.error);
      };
      trx.objectStore('subscription').getAll(IDBKeyRange.bound([topicName, '-'], [topicName, '~'])).onsuccess = (event) => {
        if (callback) {
          event.target.result.forEach((topic) => {
            callback.call(context, topic);
          });
        }
        resolve(event.target.result);
      };
    });
  }

  // Messages.
  addListMessageIndex(listMsg) {
    if (!this.isReady()) {
      return this.disabled ?
        Promise.resolve() :
        Promise.reject(new Error("not initialized"));
    }

    return new Promise((resolve, reject) => {
      const trx = this.db.transaction(['message_index'], 'readwrite');

      const objectStoreRequest = trx.objectStore('message_index');

      listMsg.forEach((msg) => {
        objectStoreRequest.add(DB.#serializeMessageIndex(null, msg));
      })

      trx.oncomplete = (event) => {
        resolve(event.target.result);
      }

      trx.commit();
    })
  }


  addMessageIndex(msg) {
    if (!this.isReady()) {
      return this.disabled ?
        Promise.resolve() :
        Promise.reject(new Error("not initialized"));
    }
    return new Promise((resolve, reject) => {
      const trx = this.db.transaction(['message_index'], 'readwrite');

      const objectStoreRequest = trx.objectStore('message_index').add(DB.#serializeMessageIndex(null, msg));

      objectStoreRequest.onsuccess = (event) => {
        resolve(event.target.result);
      }

      objectStoreRequest.onerror = (event) => {
        this.#logger('PCache', 'addMessage index', event.target.error);
        resolve({
          err: event.target.error
        });
      }

      trx.commit();
    })
  }

  addListMessage(listMsg) {
    if (!this.isReady()) {
      return this.disabled ?
        Promise.resolve() :
        Promise.reject(new Error("not initialized"));
    }

    return new Promise((resolve, reject) => {
      const trx = this.db.transaction(['message'], 'readwrite');
      const objectStoreRequest = trx.objectStore('message');

      let request

      listMsg.forEach((msg) => {
        request = objectStoreRequest.add(DB.#serializeMessage(null, msg))

        request.onsuccess = (event) => {
          resolve(event.target.result);
        }
      })

      // trx.oncomplete = (event) => {
      //   resolve(event.target.result);
      // }

      trx.commit();
    });
  }

  /**
   * Save message to persistent cache.
   * @memberOf DB
   * @param {string} topicName - name of the topic which owns the message.
   * @param {Object} msg - message to save.
   * @return {Promise} promise resolved/rejected on operation completion.
   */
  addMessage(msg) {
    if (!this.isReady()) {
      return this.disabled ?
        Promise.resolve() :
        Promise.reject(new Error("not initialized"));
    }

    return new Promise((resolve, reject) => {
      const trx = this.db.transaction(['message'], 'readwrite');
      const objectStoreRequest = trx.objectStore('message').add(DB.#serializeMessage(null, msg));

      objectStoreRequest.onsuccess = (event) => {
        resolve(event.target.result);
      }

      objectStoreRequest.onerror = (event) => {
        this.#logger('PCache', 'addMessage', event.target.error);
        reject(event.target.error);
      }

      trx.commit();
    });
  }

  updMessage(topicName, seq, msg) {
    console.log('updMessageStatus', seq, status, delivered)
    if (!this.isReady()) {
      return this.disabled ?
        Promise.resolve() :
        Promise.reject(new Error("not initialized"));
    }
    return new Promise((resolve, reject) => {
      const trx = this.db.transaction(['message'], 'readwrite');
      trx.onsuccess = event => {
        resolve(event.target.result);
      };
      trx.onerror = event => {
        this.#logger('PCache', 'updMessageStatus', event.target.error);
        reject(event.target.error);
      };
      const req = trx.objectStore('message').get(IDBKeyRange.only([topicName, seq]));
      req.onsuccess = event => {
        const src = req.result || event.target.result;
        trx.objectStore('message').put(DB.#serializeMessage(src, {
          ...msg
        }));
        trx.commit();
      };
    });
  }

  /**
   * Update delivery status of a message stored in persistent cache.
   * @memberOf DB
   * @param {string} topicName - name of the topic which owns the message.
   * @param {number} seq - ID of the message to update
   * @param {number} status - new delivery status of the message.
   * @return {Promise} promise resolved/rejected on operation completion.
   */
  updMessageStatus(topicName, seq, status, delivered, seen) {
    console.log('updMessageStatus', seq, status, delivered)
    if (!this.isReady()) {
      return this.disabled ?
        Promise.resolve() :
        Promise.reject(new Error("not initialized"));
    }
    return new Promise((resolve, reject) => {
      const trx = this.db.transaction(['message'], 'readwrite');
      trx.onsuccess = event => {
        resolve(event.target.result);
      };
      trx.onerror = event => {
        this.#logger('PCache', 'updMessageStatus', event.target.error);
        reject(event.target.error);
      };
      const req = trx.objectStore('message').get(IDBKeyRange.only([topicName, seq]));
      req.onsuccess = event => {
        const src = req.result || event.target.result;
        if (!src || src._status == status) {
          trx.commit();
          return;
        }
        trx.objectStore('message').put(DB.#serializeMessage(src, {
          topic: topicName,
          delivered: delivered,
          seq: seq,
          _status: status,
          seen,
        }));
        trx.commit();
      };
    });
  }

  /**
   * Remove one or more messages from persistent cache.
   * @memberOf DB
   * @param {string} topicName - name of the topic which owns the message.
   * @param {number} from - id of the message to remove or lower boundary when removing range (inclusive).
   * @param {number=} to - upper boundary (exclusive) when removing a range of messages.
   * @return {Promise} promise resolved/rejected on operation completion.
   */
  remMessages(topicName, from, to) {
    if (!this.isReady()) {
      return this.disabled ?
        Promise.resolve() :
        Promise.reject(new Error("not initialized"));
    }
    return new Promise((resolve, reject) => {
      if (!from && !to) {
        from = 0;
        to = Number.MAX_SAFE_INTEGER;
      }
      const range = to > 0 ? IDBKeyRange.bound([topicName, from], [topicName, to], false, true) :
        IDBKeyRange.only([topicName, from]);
      const trx = this.db.transaction(['message'], 'readwrite');
      trx.onsuccess = (event) => {
        resolve(event.target.result);
      };
      trx.onerror = (event) => {
        this.#logger('PCache', 'remMessages', event.target.error);
        reject(event.target.error);
      };
      trx.objectStore('message').delete(range);
      trx.commit();
    });
  }

  /**
   * Retrieve messages from persistent store.
   * @memberOf DB
   * @param {string} topicName - name of the topic to retrieve messages from.
   * @param {function} callback to call for each retrieved message.
   * @param {Object} query - parameters of the message range to retrieve.
   * @param {number=} query.since - the least message ID to retrieve (inclusive).
   * @param {number=} query.before - the greatest message ID to retrieve (exclusive).
   * @param {number=} query.limit - the maximum number of messages to retrieve.
   * @return {Promise} promise resolved/rejected on operation completion.
   */
  readMessages(topicName, query, callback, context) {
    if (!this.isReady()) {
      return this.disabled ?
        Promise.resolve([]) :
        Promise.reject(new Error("not initialized"));
    }
    return new Promise((resolve, reject) => {
      query = query || {};
      const since = query.since > 0 ? query.since : 0;
      const before = query.before > 0 ? query.before : Number.MAX_SAFE_INTEGER;
      const limit = query.limit | 0;

      const result = [];
      const range = IDBKeyRange.bound([topicName, since], [topicName, before], false, true);
      const trx = this.db.transaction(['message']);
      trx.onerror = (event) => {
        this.#logger('PCache', 'readMessages', event.target.error);
        reject(event.target.error);
      };
      // Iterate in descending order.
      trx.objectStore('message').openCursor(range, 'prev').onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          if (callback) {
            callback.call(context, cursor.value);
          }
          result.push(cursor.value);
          if (limit <= 0 || result.length < limit) {
            cursor.continue();
          } else {
            resolve(result);
          }
        } else {
          resolve(result);
        }
      };
    });
  }

  // Private methods.

  // Serializable topic fields.
  static #topic_fields = ['created', 'updated', 'deleted', 'read', 'recv', 'seq', 'clear', 'defacs',
    'creds', 'public', 'trusted', 'private', 'touched', '_deleted', 'sub', 'last_update', 'isOfficial',
    'pinMessage', 'brgId', 'isPin', 'shareImage', 'shareLink', 'shareFile', 'isBlockUser', 'isHide', 'isCrypt', 'stranger'
  ];

  static #saved_message_fields = ['savedTime', 'topic', 'content']

  // Copy data from src to Topic object.
  static #deserializeTopic(topic, src) {
    DB.#topic_fields.forEach((f) => {
      if (src.hasOwnProperty(f)) {
        topic[f] = src[f];
      }
    });
    if (Array.isArray(src.tags)) {
      topic._tags = src.tags;
    }
    if (src.acs) {
      topic.setAccessMode(src.acs);
    }
    topic.seq |= 0;
    topic.read |= 0;
    topic.unread = Math.max(0, topic.seq - topic.read);
  }

  // Copy values from 'src' to 'dst'. Allocate dst if it's null or undefined.
  static #serializeTopic(dst, src) {
    const res = dst || {
      name: src.name
    };
    DB.#topic_fields.forEach((f) => {
      if (src.hasOwnProperty(f)) {
        res[f] = src[f];
      }
    });
    if (Array.isArray(src._tags)) {
      res.tags = src._tags;
    }
    if (src.acs) {
      res.acs = src.getAccessMode().jsonHelper();
    }
    return res;
  }

  // Copy values from 'src' to 'dst'. Allocate dst if it's null or undefined.
  static #serializeSavedMessage(dst, src) {
    const res = dst || {
      savedTime: src.savedTime
    };
    DB.#saved_message_fields.forEach((f) => {
      if (src.hasOwnProperty(f)) {
        res[f] = src[f];
      }
    });
    if (Array.isArray(src._tags)) {
      res.tags = src._tags;
    }
    if (src.acs) {
      res.acs = src.getAccessMode().jsonHelper();
    }
    return res;
  }

  static #serializeSubscription(dst, topicName, uid, sub) {
    const fields = ['updated', 'mode', 'read', 'recv', 'clear', 'lastSeen', 'userAgent'];
    const res = dst || {
      topic: topicName,
      uid: uid
    };

    fields.forEach((f) => {
      if (sub.hasOwnProperty(f)) {
        res[f] = sub[f];
      }
    });

    return res;
  }

  static #serializeMessage(dst, msg) {
    // Serializable fields.
    const fields = ['topic', 'seq', 'ts', '_status', 'from', 'head', 'content', 'id', 'delivered', 'type', 'subtype', 'motions', 'myMotion', 'seen', 'tags', 'previewLink', 'tagAll'];
    const res = dst || {};
    fields.forEach((f) => {
      if (msg.hasOwnProperty(f)) {
        res[f] = msg[f];
      }
    });
    return res;
  }

  static #serializePollMessage(dst, msg) {
    // Serializable fields.
    const fields = ['anonymous', 'choice', 'close', 'createdAt', 'creator', 'enableAddVote', 'groupId', 'id', 'options', 'pin', 'title'];
    const res = dst || {};
    fields.forEach((f) => {
      if (msg.hasOwnProperty(f)) {
        res[f] = msg[f];
      }
    });
    return res;
  }

  static #serializeMessageIndex(dst, msg) {
    // Serializable fields.
    const fields = ['topic', 'id', 'seq'];
    const res = dst || {};
    fields.forEach((f) => {
      if (msg.hasOwnProperty(f)) {
        res[f] = msg[f];
      }
    });
    return res;
  }

  /**
   * To use DB in a non browser context, supply indexedDB provider.
   * @static
   * @memberof DB
   * @param idbProvider indexedDB provider, e.g. for node <code>require('fake-indexeddb')</code>.
   */
  static setDatabaseProvider(idbProvider) {
    IDBProvider = idbProvider;
  }

  /**
  * Hàm update motion
  * topic: topic hiện tại đang cần update
  * motionId: id motion cần thêm
  * member: người thả motion
  */

  updMessageMotion(topic, motionId, member, type) {
    const { seq, topic: topicName } = topic;
    if (!this.isReady()) {
      return this.disabled ?
        Promise.resolve() :
        Promise.reject(new Error("not initialized"));
    }
    return new Promise((resolve, reject) => {
      const trx = this.db.transaction(['message'], 'readwrite');
      trx.onsuccess = event => {
        resolve(event.target.result);
      };
      trx.onerror = event => {
        this.#logger('PCache', 'updMessage', event.target.error);
        reject(event.target.error);
      };
      const req = trx.objectStore('message').get(IDBKeyRange.only([topicName, seq]));

      req.onsuccess = event => {
        const src = req.result || event.target.result;
        if (!src) {
          reject(new Error("not found message"));
          return
        }
        let upd = [];
        let motions = src?.motions;
        if (type == 'add') {
          let motion = {
            motionId,
            member
          }
          if (motions && typeof motions === 'object') {
            const id = motions.findIndex(v => v.member === member);
            if (id != -1) {
              motions[id] = motion;
            } else {
              motions.push(motion);
            }
            upd = { motions: motions };
          } else {
            upd = { motions: [motion] }
          }
        } else if (type == 'remove') {
          const newMotions = Array.isArray(motions) ? motions.filter(v => v.member !== member) : []
          upd = { motions: newMotions };
        }

        const p = trx.objectStore('message').put(DB.#serializeMessage(src, upd));
        trx.commit();
        p.onsuccess = (event) => {
          resolve(src)
        }
      };
      req.onerror = event => {
        console.log(event)
      }
    });
  }

  /**
 * Save to cache or update savedMessage in persistent cache.
 * @memberOf DB
 * @param {Object} savedMessage - savedMessage to be added or updated.
 * @returns {Promise} promise resolved/rejected on operation completion.
 */
  updSavedMessage(savedMessage) {
    if (!this.isReady()) {
      return this.disabled ?
        Promise.resolve() :
        Promise.reject(new Error("not initialized"));
    }
    return new Promise((resolve, reject) => {
      const trx = this.db.transaction(['saved_message'], 'readwrite');
      trx.oncomplete = event => {
        resolve(event.target.result);
      };
      trx.onerror = event => {
        this.#logger('PCache', 'updSavedMessage', event.target.error);
        reject(event.target.error);
      };
      const req = trx.objectStore('saved_message').get(savedMessage.savedTime);
      req.onsuccess = _ => {
        trx.objectStore('saved_message').put(DB.#serializeSavedMessage(req.result, savedMessage));
        trx.commit();
      };
    });
  }

  /**
 * Remove topic from persistent cache.
 * @memberOf DB
 * @param {string} name - name of the topic to remove from database.
 * @return {Promise} promise resolved/rejected on operation completion.
 */
  remSavedMessage(savedTime) {
    if (!this.isReady()) {
      return this.disabled ?
        Promise.resolve() :
        Promise.reject(new Error("not initialized"));
    }
    return new Promise((resolve, reject) => {
      const trx = this.db.transaction(['saved_message'], 'readwrite');
      trx.oncomplete = event => {
        resolve(event.target.result);
      };
      trx.onerror = event => {
        this.#logger('PCache', 'remSavedMessage', event.target.error);
        reject(event.target.error);
      };
      trx.objectStore('saved_message').delete(IDBKeyRange.only(savedTime));
      trx.commit();
    });
  }

  /**
 * Execute a callback for each stored topic.
 * @memberOf DB
 * @param {function} callback - function to call for each topic.
 * @param {Object} context - the value or <code>this</code> inside the callback.
 * @return {Promise} promise resolved/rejected on operation completion.
 */
  mapSavedMessage(callback, context) {
    return this.#mapObjects('saved_message', callback, context);
  }

  updMessageByMessageIndex(id, data) {
    return this.getMessageIndex(id).then((msgIndex) => {
      const { seq, topic } = msgIndex;
      return this.updMessage(topic, seq, data);
    });
  }

  updMessage(topicName, seq, obj) {
    if (!this.isReady()) {
      return this.disabled ?
        Promise.resolve() :
        Promise.reject(new Error("not initialized"));
    }
    return new Promise((resolve, reject) => {
      const trx = this.db.transaction(['message'], 'readwrite');
      trx.onsuccess = event => {
        resolve(event.target.result);
      };
      trx.onerror = event => {
        this.#logger('PCache', 'updMessageStatus', event.target.error);
        reject(event.target.error);
      };
      const req = trx.objectStore('message').get(IDBKeyRange.only([topicName, seq]));
      req.onsuccess = event => {
        const src = req.result || event.target.result;
        trx.objectStore('message').put(DB.#serializeMessage(src, obj));
        trx.commit();
        resolve(src);
      };
    });
  }

  getPollMessages(id) {
    if (!this.isReady()) {
      return this.disabled ?
        Promise.resolve() :
        Promise.reject(new Error("not initialized"));
    }
    return new Promise((resolve, reject) => {
      const trx = this.db.transaction(['poll_message'], 'readonly');
      trx.onerror = event => {
        this.#logger('PCache', 'getMsgIndex', event.target.error);
        reject(event.target.error);
      };
      trx.objectStore('poll_message').get([id]).onsuccess = (event) => {
        const msgPoll = event.target.result;
        resolve(msgPoll);
      };
    });
  }

  addPollMessage(msg) {
    if (!this.isReady()) {
      return this.disabled ?
        Promise.resolve() :
        Promise.reject(new Error("not initialized"));
    }

    return new Promise((resolve, reject) => {
      const trx = this.db.transaction(['poll_message'], 'readwrite');
      const objectStoreRequest = trx.objectStore('poll_message').add(DB.#serializePollMessage(null, msg));

      objectStoreRequest.onsuccess = (event) => {
        resolve(event.target.result);
      }

      objectStoreRequest.onerror = (event) => {
        this.#logger('PCache', 'poll_message', event.target.error);
        reject(event.target.error);
      }

      trx.commit();
    });
  }

  updPollMessage(msg) {
    if (!this.isReady()) {
      return this.disabled ?
        Promise.resolve() :
        Promise.reject(new Error("not initialized"));
    }
    return new Promise((resolve, reject) => {
      const trx = this.db.transaction(['poll_message'], 'readwrite');
      trx.onsuccess = event => {
        resolve(event.target.result);
      };
      trx.onerror = event => {
        this.#logger('PCache', 'updMessageStatus', event.target.error);
        reject(event.target.error);
      };
      const req = trx.objectStore('poll_message').get(IDBKeyRange.only([msg.id]));
      req.onsuccess = event => {
        const src = req.result || event.target.result;
        trx.objectStore('poll_message').put(DB.#serializePollMessage(src, msg));
        trx.commit();
        resolve(src);
      };
    });
  }

  log(message) {
    if (!this.isReady()) {
      return this.disabled ?
        Promise.resolve() :
        Promise.reject(new Error("not initialized"));
    }

    return new Promise((resolve, reject) => {
      const trx = this.db.transaction(['weblog'], 'readwrite');

      const msg = {
        message,
        time: new Date().getTime(),
        rand: StringUtils.randomString(5),
      }

      const objectStoreRequest = trx.objectStore('weblog').add(msg);

      objectStoreRequest.onsuccess = (event) => {
        resolve(event.target.result);
      }

      objectStoreRequest.onerror = (event) => {
        this.#logger('PCache', 'addLog', event.target.error);
        reject(event.target.error);
      }

      trx.commit();
    }).catch((err) => {
      // xu ly truong hop mat ket noi voi DB
      if (err.name === "InvalidStateError") {
        this.initDatabase()
      }
    });
  }

  mapLogs(callback, context) {
    return this.#mapObjects('weblog', callback, context);
  }

  remLog(time) {
    if (!this.isReady()) {
      return this.disabled ?
        Promise.resolve() :
        Promise.reject(new Error("not initialized"));
    }
    return new Promise((resolve, reject) => {
      const trx = this.db.transaction(['weblog'], 'readwrite');
      trx.oncomplete = event => {
        resolve(event.target.result);
      };
      trx.onerror = event => {
        this.#logger('PCache', 'remLog', event.target.error);
        reject(event.target.error);
      };
      const objStore = trx.objectStore('weblog')
      const cursorRequest = objStore.openCursor()
      cursorRequest.onsuccess = (event) => {
        let cursor = event.target.result
        if (cursor) {
          if (cursor.key[0] < time) {
            objStore.delete(cursor.key)
          }
          cursor.continue()
        }
      }

      cursorRequest.onerror = (event) => {
        this.#logger('PCache', 'remLog', event.target.error);
      }

    });
  }

  /**
   * Check indexedDB is exists
   * 
   * @returns boolean
   */
  async checkDBExists() {
    if (!indexedDB.databases) return true; // assume it exists
    const dbs = await indexedDB.databases();
    return dbs.some(db => db.name === DB_NAME);
  }

  async ensurePersistentStorage() {
    const isPersisted = await navigator.storage.persisted();
    if (!isPersisted) {
      const granted = await navigator.storage.persist();
      if (granted) {
        console.log("Persistent Storage have been granted.");
        this.#logger('PersistentStorage', 'ensurePersistentStorage|result: true');
      } else {
        this.#logger('PersistentStorage', 'ensurePersistentStorage|result: true');
        console.log("Persistent Storage cannot be granted.");
      }
    } else {
      this.#logger('PersistentStorage', 'ensurePersistentStorage|persisted');
      console.log("Persistent Storage available.");
    }
  }
}
